import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function SeftonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Sefton Pothole Claims | 22% of U-Roads in RED | Fixtyer"
        description="Sefton Council earns GREEN on spend but AMBER overall — 22% of its 741km of estate roads were in RED at the last survey, with 12,828 repair jobs in five years. See what the DfT rating means for your pothole claim."
        canonical="/councils/sefton"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Sefton', url: '/councils/sefton' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Sefton has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Sefton projects £6.07m capital spend against a £1.46m DfT allocation in 2025/26. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. Sefton\'s own condition data shows 18–22% of U-roads in RED at the last two surveys, repair jobs rising to 3,102 in 2024/25, and reactive maintenance averaging 29% of the annual budget.',
          },
          {
            question: 'What if my pothole was on a housing estate or residential road?',
            answer: 'U-roads make up 741km — 77.5% of Sefton\'s 956km carriageway network. The council publishes only RED-condition percentages for U-roads via Course Visual Inspections, not full amber/green breakdowns. At the last two comparable surveys, 22% (2021) and 18% (2023) of U-roads were in RED — roughly 133–163km of estate streets. Planned maintenance is prioritised A roads first, then B, then C, then other roads — U-roads come last.',
          },
          {
            question: 'Why are there gaps in Sefton\'s five-year condition data?',
            answer: 'Sefton\'s own report states that where budgets are restricted, condition surveys have been commissioned every two years. For classified A, B and C roads, no surveys were commissioned in 2020, 2022 or 2024. For U-roads, no surveys were commissioned in 2020, 2022 or 2024 either. If your incident falls in a blind year, there is no network-level condition data for that period.',
          },
          {
            question: 'Sefton\'s repair job count fell then rose again — does that mean the roads are fixed?',
            answer: 'No. Sefton recorded 3,102 repair jobs in 2024/25 — up from 2,026 in 2022/23 and the highest in five years. The council explicitly states these are "repair jobs" not pothole counts, and that its asset management system "does not allow easy extraction of pothole quantities". A single repair job may cover multiple defects. Twelve thousand eight hundred and twenty-eight repair jobs over five years on a 956km network tells you defects keep forming.',
          },
          {
            question: 'Does Sefton using insurance claims in road prioritisation help my claim?',
            answer: 'It cuts both ways. Sefton\'s prioritisation scoring includes carriageway condition, safety defects, insurance claims, hierarchy, road environment, customer feedback and affordability. The council states its preventative approach aims to "reduce the number of potholes appearing and maximising the condition of the road network, which reduces the insurance claims made against the Council." If you have already claimed for the same road, the council may have documented notice. If you have not, the absence of a prior claim on a road with heavy reactive repair history strengthens the argument the defect was not proactively addressed.',
          },
          {
            question: 'How does Section 58 apply to Sefton\'s inspection regime?',
            answer: 'Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance systems. Sefton follows the Well Managed Highway Infrastructure Code with continuous inspector monitoring alongside periodic SCANNER and CVI surveys. Your claim succeeds when evidence shows the specific defect met intervention criteria, was previously reported, or should have been found before you hit it — especially on U-roads where failure levels "remain high when compared to condition levels across the classified road network" in the council\'s own words.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Sefton Council"
        councilSlug="sefton"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/sefton"
        headline="Sefton Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Sefton</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Sefton: Estate Roads Surveyed Last, 22% in RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Sefton Council projects <strong className="text-emerald-400">£6.07m capital spend</strong> against a{' '}
            <strong className="text-emerald-400">£1.46m DfT allocation</strong> and earns a GREEN spend scorecard — yet
            the overall rating is <strong className="text-amber-400">AMBER</strong>. Their own transparency report shows{' '}
            <strong className="text-rose-400">22% of U-roads in RED at the last survey</strong> on a network where
            741km of housing-estate roads make up 77.5% of the borough, and{' '}
            <strong className="text-rose-400">12,828 repair jobs in five years</strong>.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">22%</div>
            <div className="text-white font-medium mt-2">U-roads in RED condition (2021 survey)</div>
            <div className="text-brand-300 text-sm mt-1">
              Down to 18% at the 2023 survey — but the council itself says failure levels on unclassified roads
              "remain high when compared to condition levels across the classified road network." Roughly 133–163km
              of estate streets on Sefton's 741km U-road network.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network size */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A 956km Network Dominated by Estate Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Sefton Metropolitan Borough Council maintains 956km of adopted carriageway — and most of it is residential
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center font-mono">110</td>
                  <td className="px-6 py-4 text-center">11.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center font-mono">103</td>
                  <td className="px-6 py-4 text-center">10.8%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-bold text-amber-900">U roads (housing estates)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">741</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">77.5%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">956</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Classified Roads Carry the Traffic</h3>
              <p className="text-surface-600 text-sm">
                A, B and C roads — 213km combined — carry most journeys and account for 22% of the network.
                SCANNER laser surveys cover 100% of this classified network when commissioned.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">U-Roads Are the Borough</h3>
              <p className="text-amber-800 text-sm">
                The remaining 741km are unclassified roads — "basically the housing estates across the borough."
                They carry much less traffic but serve movement and public-realm functions in Sefton's distinctive
                neighbourhoods. If your pothole was on a residential street, it was almost certainly a U-road.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of survey data from Sefton's own transparency report — classified roads steady, U-roads failing high
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (110km — 11.5% of network): steady at 3% RED</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey commissioned</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-surface-50">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey commissioned</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                  <tr className="bg-surface-50">
                    <td className="px-4 py-2 font-mono">2024</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No survey commissioned</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road condition is broadly stable — but surveys only run in alternate years when budgets allow.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (103km — 10.8% of network): RED edging up</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">77%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C RED sections rose from 2% to 3% between comparable surveys. The council notes RED sections
              "have remained in a steady state" — but amber sections crossing into red replace those treated.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (741km — 77.5% of network): failure levels remain high</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-6 py-3 text-left text-sm font-semibold text-amber-900">Year</th>
                    <th className="px-6 py-3 text-center text-sm font-semibold text-rose-700">U-roads in RED condition</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-amber-100 bg-amber-50/50">
                    <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                    <td className="px-6 py-4 text-center text-surface-400 italic">No survey commissioned</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">22%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-amber-50/50">
                    <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                    <td className="px-6 py-4 text-center text-surface-400 italic">No survey commissioned</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                    <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-mono text-brand-900">2024</td>
                    <td className="px-6 py-4 text-center text-surface-400 italic">No survey commissioned</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Course Visual Inspections only publish a RED-equivalent "failed carriageway" percentage for U-roads —
              not amber or green breakdowns. At 18–22% RED, roughly one in five estate roads needs maintenance now.
              That is 7–10 times the RED rate on classified A-roads.
            </p>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Generally, across the unclassified road network, Sefton Council's 'Red', i.e. 'failed' sections of the network has been reduced over the course of the past 5 years. However, failure levels remain high when compared to condition levels across the classified road network."
              source="Sefton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Alternate-Year Survey Gap</h3>
            <p className="text-brand-200 mb-4">
              Sefton states it undertakes SCANNER surveys to 100% of its classified network annually — but adds:
            </p>
            <QuoteCard
              quote="Where budgets are restricted, condition surveys have been commissioned every two years."
              source="Sefton Council Local Highways Maintenance Transparency Report 2025"
            />
            <p className="text-brand-300 mt-4 text-sm">
              In practice, neither A/B/C nor U-road surveys were commissioned in 2020, 2022 or 2024. For incidents
              in those years, there is no network-level condition data at all — on a borough where 77.5% of roads
              are housing estates surveyed only via periodic visual inspections.
            </p>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Spending: GREEN Scorecard, Rising Reactive Share
          </h2>
          <p className="text-surface-600 mb-8">
            Sefton invests well beyond its DfT allocation — but reactive maintenance is climbing again
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£167,400</td>
                  <td className="px-4 py-3 text-center">£3,749,400</td>
                  <td className="px-4 py-3 text-center">£3,591,000</td>
                  <td className="px-4 py-3 text-center">65%</td>
                  <td className="px-4 py-3 text-center">35%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">£2,820,750</td>
                  <td className="px-4 py-3 text-center">£3,571,250</td>
                  <td className="px-4 py-3 text-center">70%</td>
                  <td className="px-4 py-3 text-center">30%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">£4,705,000</td>
                  <td className="px-4 py-3 text-center">£3,471,000</td>
                  <td className="px-4 py-3 text-center">86%</td>
                  <td className="px-4 py-3 text-center">14%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£628,487</td>
                  <td className="px-4 py-3 text-center">£5,797,382</td>
                  <td className="px-4 py-3 text-center">£3,530,000</td>
                  <td className="px-4 py-3 text-center">82%</td>
                  <td className="px-4 py-3 text-center">18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£898,604</td>
                  <td className="px-4 py-3 text-center">£5,624,455</td>
                  <td className="px-4 py-3 text-center">£2,580,000</td>
                  <td className="px-4 py-3 text-center">61%</td>
                  <td className="px-4 py-3 text-center">39%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (predicted)</td>
                  <td className="px-4 py-3 text-center font-bold">£1,458,556</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£6,065,556</td>
                  <td className="px-4 py-3 text-center">£2,895,000</td>
                  <td className="px-4 py-3 text-center">64%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">36%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Why Spend Is GREEN</h3>
              <p className="text-emerald-800 text-sm">
                Sefton projects £6.07m capital spend against a £1.46m DfT allocation in 2025/26 — over four times
                the central grant, before CRSTS funding of £4.61m. Total budget allocation is £8.96m. The council
                received an additional £1,458,556 specifically for 2025/26 highway maintenance.
              </p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-6">
              <h3 className="font-semibold text-rose-900 mb-3">Why Reactive Share Matters</h3>
              <p className="text-rose-800 text-sm">
                The council reports an average of 29% of the annual budget on reactive maintenance — pothole repairs
                and emergency call-outs. That share fell to 14% in 2022/23 but has climbed back to a predicted 36%
                in 2025/26. More reactive spend means more defects forming faster than planned works prevent them.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="The 'Highway Maintenance Spending' table shown above indicates an average of 29% of the annual budget is spent on reactive maintenance, it is anticipated that the extra funding made available on the basis of this report will be used to support expanded works programmes to such an extent that Sefton Borough Council sees some reduction of this reactive spend."
            source="Sefton Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Repair jobs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            12,828 Repair Jobs in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volume is rising — and the council cannot even count individual potholes
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Repair jobs raised</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,398</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,430</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,026</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,872</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,102</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12,828</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Sefton Borough Council's asset management system does not allow easy extraction of pothole quantities and as a consequence the figures above are the number of 'repair jobs' raised by the highway inspectors – please note a repair job may include more than one defect if such quantities are identified outside the same location."
              source="Sefton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~7 Repair Jobs Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Sefton raises around seven repair jobs per day across its 956km network.
                The 2024/25 figure of 3,102 is the highest in the published series — roughly 8.5 jobs per day.
                Defects are forming faster than the council's own counting systems can track them individually.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Repair Jobs ≠ Potholes</h3>
              <p className="text-amber-800 text-sm">
                The council's footnote is explicit: these are inspector-raised repair jobs, not pothole counts,
                and one job may cover multiple defects. The true number of individual potholes treated is therefore
                <strong> higher</strong> than 12,828 — but Sefton cannot tell you how much higher.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Prioritisation admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Estate Roads Come Last in the Queue
          </h2>
          <p className="text-surface-600 mb-8">
            Sefton's own prioritisation hierarchy — and what it means for U-road claims
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Planned maintenance programmes are prioritised by using a risk-based approach and following the hierarchal categories of A roads first, followed by B roads, then C roads, and then other roads, when developing the priority programme. This ensures that the funding is used on the roads of higher risk."
              source="Sefton Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The priorities for scheme selection are based on several weighting factors that create a ranking score; they include carriageway condition, number of safety defects, insurance claims, hierarchy, road environment, customer feedback and affordability."
              source="Sefton Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What This Means in Practice</h3>
              <p className="text-surface-600 text-sm mb-3">
                With 741km of U-roads and only 16.1km of resurfacing predicted for 2025/26 across the entire network,
                estate streets compete for scraps after A, B and C roads are served. The council's 2025/26 programme
                plans 10.8km of preventative carriageway treatment and 16.1km of resurfacing — combined, that treats
                roughly 2.8% of the total 956km network in a single year.
              </p>
              <p className="text-surface-600 text-sm">
                Insurance claims are an explicit prioritisation factor — so a prior claim on your road may mean
                documented council notice, while the absence of one on a high-RED U-road strengthens your case.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Claims Reduction Aim</h3>
              <QuoteCard
                quote="The funding will continue to be prioritised on preventative and planned maintenance over reactive maintenance with the aim to reduce the number of potholes appearing and maximising the condition of the road network, which reduces the insurance claims made against the Council."
                source="Sefton Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing the
              condition of its roads. For Sefton's U-road network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your estate road last condition-surveyed — and was it a blind year (2020, 2022 or 2024)?</li>
              <li>• If 18–22% of U-roads were RED at the last comparable survey, what was done about yours?</li>
              <li>• Does A-first prioritisation explain why a known-failing U-road was not resurfaced?</li>
              <li>• Were prior reports or insurance claims on your road logged in the prioritisation scoring?</li>
              <li>• From 2026/27, PAS 2161 will change condition categories — can pre-change records be compared?</li>
            </ul>
            <p className="text-white font-medium">
              A council that puts 77.5% of its network last in the maintenance queue cannot claim equal attention
              to estate streets and main roads.
            </p>
          </div>
        </div>
      </section>

      {/* Forward plans */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2025/26 Plans and the PAS 2161 Change Ahead
          </h2>
          <p className="text-surface-600 mb-8">
            What Sefton says it will do next — and the methodology shift coming in 2026/27
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Resurfacing (km)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative (km)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Footways (km)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">10.0</td>
                  <td className="px-4 py-3 text-center">5.0</td>
                  <td className="px-4 py-3 text-center">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">11.0</td>
                  <td className="px-4 py-3 text-center">25.0</td>
                  <td className="px-4 py-3 text-center">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">19.0</td>
                  <td className="px-4 py-3 text-center">12.0</td>
                  <td className="px-4 py-3 text-center">6.4</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">15.0</td>
                  <td className="px-4 py-3 text-center">9.1</td>
                  <td className="px-4 py-3 text-center">11.5</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">17.0</td>
                  <td className="px-4 py-3 text-center">9.3</td>
                  <td className="px-4 py-3 text-center">1.0</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (predicted)</td>
                  <td className="px-4 py-3 text-center font-bold">16.1</td>
                  <td className="px-4 py-3 text-center font-bold">10.8</td>
                  <td className="px-4 py-3 text-center">14.1</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Additional DfT Funding</h3>
              <p className="text-surface-600 text-sm">
                Sefton's £1,458,556 additional DfT allocation for 2025/26 will "support the completion of the
                councils 2025/26 approved highway maintenance programme, ensuring all schemes listed are completed
                in their entirety." The council notes that while filling individual potholes provides short-term
                relief, resurfacing addresses the root cause — yet only 16.1km of resurfacing is planned across
                the entire 956km network.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS 2161 From 2026/27</h3>
              <p className="text-amber-800 text-sm">
                Sefton notes that from 2026/27 a new BSI PAS 2161 methodology will categorise roads into five
                condition categories instead of three. Local authorities must use PAS 2161-accredited suppliers.
                Condition records before and after that change may not be directly comparable — capture your
                evidence now while the current RED/amber/green framework still applies.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Sefton is not a RED-rated authority — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend over four times the DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Asset management approach with risk-based prioritisation and ISO-aligned practices
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Classified A-roads stable at 3% RED with 76–77% green
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED down from 22% to 18% between 2021 and 2023 surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Continuous inspector monitoring under Well Managed Highway Infrastructure Code
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on main roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 18–22% of U-roads in RED at last surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 77.5% of network is U-roads prioritised after A, B and C
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Alternate-year survey gaps in 2020, 2022 and 2024
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 12,828 repair jobs in five years — rising to 3,102 in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive share climbing back to 36% — council cannot count individual potholes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits U-road "failure levels remain high" vs classified network
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a documented asset management strategy, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the prioritisation hierarchy and survey gaps are your strongest structural arguments</li>
              <li>• Whether your road sits in an area where 18–22% of comparable streets were already RED at last survey</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Sefton's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Sefton?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road prioritisation argument</li>
                <li>• No prior-report search</li>
                <li>• No alternate-year survey-gap analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 18–22% U-road RED condition documented</li>
                <li>✅ Estate-road prioritisation hierarchy argued</li>
                <li>✅ 12,828 repair jobs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Sefton</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=sefton"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Sefton Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Sefton has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Sefton projects £6.07m capital spend against a £1.46m
                DfT allocation in 2025/26. Section 58 turns on whether the specific defect that damaged your vehicle
                was reasonably inspected and repaired — not on aggregate spend. Sefton's own condition data shows 18–22%
                of U-roads in RED at the last two surveys, repair jobs rising to 3,102 in 2024/25, and reactive
                maintenance averaging 29% of the annual budget.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a housing estate or residential road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 741km — 77.5% of Sefton's 956km carriageway network. The council publishes only
                RED-condition percentages for U-roads via Course Visual Inspections, not full amber/green breakdowns.
                At the last two comparable surveys, 22% (2021) and 18% (2023) of U-roads were in RED — roughly
                133–163km of estate streets. Planned maintenance is prioritised A roads first, then B, then C, then
                other roads — U-roads come last.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why are there gaps in Sefton's five-year condition data?
              </h3>
              <p className="text-surface-600 text-sm">
                Sefton's own report states that where budgets are restricted, condition surveys have been commissioned
                every two years. For classified A, B and C roads, no surveys were commissioned in 2020, 2022 or 2024.
                For U-roads, no surveys were commissioned in 2020, 2022 or 2024 either. If your incident falls in a
                blind year, there is no network-level condition data for that period.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Sefton's repair job count fell then rose again — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Sefton recorded 3,102 repair jobs in 2024/25 — up from 2,026 in 2022/23 and the highest in five
                years. The council explicitly states these are "repair jobs" not pothole counts, and that its asset
                management system "does not allow easy extraction of pothole quantities". A single repair job may cover
                multiple defects. Twelve thousand eight hundred and twenty-eight repair jobs over five years on a 956km
                network tells you defects keep forming.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Sefton using insurance claims in road prioritisation help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It cuts both ways. Sefton's prioritisation scoring includes carriageway condition, safety defects,
                insurance claims, hierarchy, road environment, customer feedback and affordability. The council states
                its preventative approach aims to "reduce the number of potholes appearing and maximising the condition
                of the road network, which reduces the insurance claims made against the Council." If you have already
                claimed for the same road, the council may have documented notice. If you have not, the absence of a
                prior claim on a road with heavy reactive repair history strengthens the argument the defect was not
                proactively addressed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                How does Section 58 apply to Sefton's inspection regime?
              </h3>
              <p className="text-surface-600 text-sm">
                Section 58 of the Highways Act 1980 lets councils defend claims by proving reasonable maintenance
                systems. Sefton follows the Well Managed Highway Infrastructure Code with continuous inspector
                monitoring alongside periodic SCANNER and CVI surveys. Your claim succeeds when evidence shows the
                specific defect met intervention criteria, was previously reported, or should have been found before
                you hit it — especially on U-roads where failure levels "remain high when compared to condition levels
                across the classified road network" in the council's own words.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.sefton.gov.uk/parking-roads-travel/street-maintenance/local-highway-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Sefton Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
